import { BadRequestException, Injectable, NotFoundException, Optional } from '@nestjs/common';
import {
  AddressListResponse,
  AddressSummary,
  CreateAddressRequest,
  UpdateAddressRequest,
} from '@cart/shared-types';
import { AuditService } from '../audit/audit.service';
import { AddressesRepository } from './addresses.repository';

@Injectable()
export class AddressesService {
  constructor(
    private readonly addressesRepository: AddressesRepository,
    @Optional()
    private readonly auditService?: AuditService
  ) {}

  async list(userId: string): Promise<AddressListResponse> {
    const items = await this.addressesRepository.list(userId);

    return {
      items,
      total: items.length,
    };
  }

  async create(userId: string, input: CreateAddressRequest): Promise<AddressSummary> {
    const address = this.normalizeAndValidate(input);

    const created = await this.addressesRepository.create(userId, address);

    await this.auditService?.logChange({
      schemaName: 'admin',
      tableName: 'addresses',
      recordId: created.id,
      operation: 'create',
      changedColumns: Object.keys(address),
      newValue: created,
      changedBy: userId,
      source: 'AddressesService.create',
    });

    return created;
  }

  async update(
    userId: string,
    addressId: string,
    input: UpdateAddressRequest
  ): Promise<AddressSummary> {
    const address = this.normalizeAndValidate(input);
    const updated = await this.addressesRepository.update(userId, addressId, address);

    if (!updated) {
      throw new NotFoundException('Address not found');
    }

    await this.auditService?.logChange({
      schemaName: 'admin',
      tableName: 'addresses',
      recordId: updated.id,
      operation: 'update',
      changedColumns: Object.keys(address),
      newValue: updated,
      changedBy: userId,
      source: 'AddressesService.update',
    });

    return updated;
  }

  async delete(userId: string, addressId: string): Promise<void> {
    const deleted = await this.addressesRepository.delete(userId, addressId);

    if (!deleted) {
      throw new NotFoundException('Address not found');
    }

    await this.auditService?.logChange({
      schemaName: 'admin',
      tableName: 'addresses',
      recordId: addressId,
      operation: 'delete',
      changedBy: userId,
      source: 'AddressesService.delete',
    });
  }

  private normalizeAndValidate(input: CreateAddressRequest): CreateAddressRequest {
    const address = {
      label: input.label?.trim(),
      recipientName: input.recipientName?.trim(),
      line1: input.line1?.trim(),
      line2: input.line2?.trim() || undefined,
      city: input.city?.trim(),
      state: input.state?.trim(),
      postalCode: input.postalCode?.trim(),
      country: input.country?.trim().toUpperCase(),
      phone: input.phone?.trim() || undefined,
    };

    const required: Array<keyof CreateAddressRequest> = [
      'label',
      'recipientName',
      'line1',
      'city',
      'state',
      'postalCode',
      'country',
    ];
    const missing = required.find((field) => !address[field]);

    if (missing) {
      throw new BadRequestException(`${missing} is required`);
    }

    return address;
  }
}
